import React from "react";
import { Check, Info, ArrowRight } from "lucide-react";
import { Section, SectionTitle, Lead } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { BenefitGrid } from "@/components/shared/BenefitGrid";

const Heading = ({ children, n }) => (
  <div className="flex items-baseline gap-4">
    {n && <span className="font-mono text-[11px] tracking-[0.24em] text-grn">{n}</span>}
    <SectionTitle className="!text-2xl sm:!text-3xl">{children}</SectionTitle>
  </div>
);

const Prose = ({ s }) => (
  <Reveal>
    {s.heading && <Heading>{s.heading}</Heading>}
    <div className="mt-6 max-w-3xl space-y-5">
      {s.body.map((p, i) => (
        <p key={i} className="text-sm leading-relaxed text-slate-600 sm:text-base">
          {p}
        </p>
      ))}
    </div>
  </Reveal>
);

const Bullets = ({ s }) => (
  <Reveal>
    {s.heading && <Heading>{s.heading}</Heading>}
    {s.intro && <Lead className="mt-5 max-w-2xl">{s.intro}</Lead>}
    <Stagger className="mt-8 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3" step={0.045}>
      {s.items.map((it) => (
        <StaggerItem key={it} className="bg-white">
          <div className="flex h-full items-start gap-3 p-6">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-grn" strokeWidth={2.4} />
            <span className="text-[14px] text-navy/85">{it}</span>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
    {s.note && (
      <p className="mt-6 max-w-2xl border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-500">{s.note}</p>
    )}
  </Reveal>
);

const Quote = ({ s }) => (
  <Reveal>
    <div className="border-y border-navy/10 py-12">
      <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-grn">{s.heading}</p>
      <p className="mt-6 max-w-4xl font-display text-2xl font-medium leading-snug text-navy sm:text-[2.1rem]">
        {s.body}
      </p>
    </div>
  </Reveal>
);

const Cards = ({ s }) => (
  <Reveal>
    {s.heading && <Heading>{s.heading}</Heading>}
    <Stagger className="mt-8 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3" step={0.05}>
      {s.items.map((it, i) => (
        <StaggerItem key={it.title} className="bg-white">
          <div className="group h-full p-8 transition-colors duration-500 hover:bg-navy">
            <span className="font-mono text-[11px] tracking-[0.2em] text-navy/25 transition-colors duration-500 group-hover:text-gold">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-6 font-display text-lg font-semibold text-navy transition-colors duration-500 group-hover:text-white">
              {it.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 transition-colors duration-500 group-hover:text-white/60">
              {it.body}
            </p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </Reveal>
);

const Numbered = ({ s }) => (
  <Reveal>
    {s.heading && <Heading>{s.heading}</Heading>}
    <div className="mt-4 divide-y divide-navy/10 border-y border-navy/10">
      {s.items.map((it, i) => (
        <div key={it.title} className="group grid gap-4 py-8 sm:grid-cols-[90px_1fr] sm:gap-10">
          <span className="font-display text-3xl font-bold leading-none text-navy/15 transition-colors duration-500 group-hover:text-grn">
            {String(i + 1).padStart(2, "0")}
          </span>
          <div>
            <h3 className="font-display text-xl font-semibold text-navy">{it.title}</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{it.body}</p>
          </div>
        </div>
      ))}
    </div>
  </Reveal>
);

const Flow = ({ s }) => (
  <Reveal>
    {s.heading && <Heading>{s.heading}</Heading>}
    <Stagger className="mt-8 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4" step={0.05}>
      {s.items.map((it, i) => (
        <StaggerItem key={it.title} className="bg-white">
          <div className="h-full p-7">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-grn">
              {String(i + 1).padStart(2, "0")}
              {i < s.items.length - 1 && <ArrowRight className="h-3 w-3 text-navy/25" />}
            </div>
            <h3 className="mt-6 font-display text-base font-semibold uppercase tracking-[0.04em] text-navy">
              {it.title}
            </h3>
            <p className="mt-3 text-[13px] leading-relaxed text-slate-600">{it.body}</p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </Reveal>
);

const Callout = ({ s }) => (
  <Reveal>
    <div className="relative overflow-hidden bg-navy p-8 text-white sm:p-12 grain" data-testid="callout">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hairline-grid" />
      <div className="relative">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
          <Info className="h-3.5 w-3.5" /> {s.heading}
        </p>
        <p className="mt-6 max-w-3xl font-display text-lg leading-relaxed sm:text-xl">{s.body}</p>
      </div>
    </div>
  </Reveal>
);

const MAP = { prose: Prose, bullets: Bullets, quote: Quote, cards: Cards, numbered: Numbered, flow: Flow, callout: Callout };

export const SectionRenderer = ({ sections = [] }) => (
  <Section className="py-20 sm:py-24">
    <div className="space-y-20 sm:space-y-24">
      {sections.map((s, i) => {
        if (s.type === "benefitGrid") return <BenefitGrid key={i} withHeading={false} />;
        const Cmp = MAP[s.type];
        return Cmp ? <Cmp key={i} s={s} /> : null;
      })}
    </div>
  </Section>
);
